// Animated background: ~700 boids flock across the canvas using
// the classic separation / alignment / cohesion rules, with a soft
// rightward migration bias. Pointer/touch acts as a "predator" the
// flock veers away from. Spatial grid keeps the neighbour query
// linear in practice so this stays cheap with 500+ agents.

export function mountFlowField(canvas, options = {}) {
  const ctx = canvas.getContext('2d', { alpha: false });
  // Narrow-viewport downgrade: 1800 boids with N² neighbour lookups
  // pegs the CPU on phones. Drop the count and bump the point size
  // so the field still reads as a dense cloud.
  const mobile = typeof window !== 'undefined'
    && !!window.matchMedia?.('(max-width: 900px)')?.matches;
  const opts = {
    count: mobile ? 500 : 1800,
    pointAlpha: 0.85,
    pointSize: mobile ? 2.0 : 1.7,  // base size at neutral depth

    // Wider vision + strong cohesion gather broad clouds; large
    // separation keeps each point visibly apart inside the cloud.
    visionR: 80,
    separationR: 18,
    wSep: 2.0,
    wAlign: 1.7,
    wCoh: 1.25,
    cohScale: 0.055,
    maxTurn: 0.05,
    turnEase: 0.32,
    speedEase: 0.08,

    maxSpeed: 0.7,
    minSpeed: 0.32,
    bias: 0,

    // 3D-feel: each particle has a z coordinate that drifts through
    // a slow noise field. Rendering scales point size by depth so
    // the flock appears to twist and warp as a volume.
    depthAmount: 1.1,       // how much z affects rendered size
    depthDrift: 0.025,      // how fast particles slide toward field z

    // Predator (pointer) interaction
    pointerRadius: 260,
    pointerStrength: 1.7,

    bg: '#0a0a0a',
    stroke: '#ffffff',
    ...options,
  };

  let w = 0, h = 0, dpr = 1;
  const particles = [];
  const pointer = { x: -9999, y: -9999, active: false };
  let raf = 0;
  let last = 0;
  let time = 0;

  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    w = rect.width;
    h = rect.height;
    canvas.width = Math.max(1, Math.floor(w * dpr));
    canvas.height = Math.max(1, Math.floor(h * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function spawn(p) {
    p.x = Math.random() * w;
    p.y = Math.random() * h;
    p.z = Math.random() * 2 - 1;   // -1 (far) .. +1 (near)
    // Small, random isotropic initial velocity.
    const angle = Math.random() * Math.PI * 2;
    const speed = opts.minSpeed + Math.random() * (opts.maxSpeed - opts.minSpeed);
    p.vx = Math.cos(angle) * speed;
    p.vy = Math.sin(angle) * speed;
  }

  function initParticles() {
    particles.length = 0;
    for (let i = 0; i < opts.count; i++) {
      const p = {};
      spawn(p);
      particles.push(p);
    }
  }

  // Spatial hash keyed by cell coords; rebuilt every frame.
  const grid = new Map();
  function rebuildGrid() {
    grid.clear();
    const c = opts.visionR;
    for (const p of particles) {
      const k = `${Math.floor(p.x / c)},${Math.floor(p.y / c)}`;
      let bucket = grid.get(k);
      if (!bucket) {
        bucket = [];
        grid.set(k, bucket);
      }
      bucket.push(p);
    }
  }

  function clampSpeed(p) {
    const sp2 = p.vx * p.vx + p.vy * p.vy;
    if (sp2 > opts.maxSpeed * opts.maxSpeed) {
      const sp = Math.sqrt(sp2);
      p.vx = (p.vx / sp) * opts.maxSpeed;
      p.vy = (p.vy / sp) * opts.maxSpeed;
    } else if (sp2 < opts.minSpeed * opts.minSpeed) {
      const sp = Math.sqrt(sp2) || 1;
      p.vx = (p.vx / sp) * opts.minSpeed;
      p.vy = (p.vy / sp) * opts.minSpeed;
    }
  }

  function step() {
    rebuildGrid();

    const cell = opts.visionR;
    const visR2 = opts.visionR * opts.visionR;
    const sepR2 = opts.separationR * opts.separationR;

    for (const p of particles) {
      const cx = Math.floor(p.x / cell);
      const cy = Math.floor(p.y / cell);
      let sx = 0, sy = 0;     // separation
      let ax = 0, ay = 0;     // alignment accumulator (velocity sum)
      let hx = 0, hy = 0;     // cohesion accumulator (position sum)
      let count = 0;

      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const bucket = grid.get(`${cx + dx},${cy + dy}`);
          if (!bucket) continue;
          for (const n of bucket) {
            if (n === p) continue;
            const ox = p.x - n.x;
            const oy = p.y - n.y;
            const d2 = ox * ox + oy * oy;
            if (d2 > visR2 || d2 === 0) continue;
            if (d2 < sepR2) {
              const inv = 1 / d2;
              sx += ox * inv;
              sy += oy * inv;
            }
            ax += n.vx;
            ay += n.vy;
            hx += n.x;
            hy += n.y;
            count++;
          }
        }
      }

      if (count > 0) {
        ax = ax / count - p.vx;
        ay = ay / count - p.vy;
        hx = hx / count - p.x;
        hy = hy / count - p.y;
      }

      // Build the candidate new velocity from boid forces.
      let nvx = p.vx + sx * opts.wSep + ax * opts.wAlign + hx * opts.wCoh * opts.cohScale + opts.bias;
      let nvy = p.vy + sy * opts.wSep + ay * opts.wAlign + hy * opts.wCoh * opts.cohScale;

      // Two-stage smoothing on direction:
      //   1. ease — only take a fraction of the desired turn each
      //      frame, so birds approach the target heading gradually
      //   2. hard cap — never exceed `maxTurn` per frame regardless
      // Result: short heading nudges read as gentle arcs, big course
      // corrections still bend smoothly without snapping.
      const oldA = Math.atan2(p.vy, p.vx);
      const newA = Math.atan2(nvy, nvx);
      let dA = newA - oldA;
      while (dA > Math.PI) dA -= 2 * Math.PI;
      while (dA < -Math.PI) dA += 2 * Math.PI;
      dA *= opts.turnEase;
      if (dA > opts.maxTurn) dA = opts.maxTurn;
      else if (dA < -opts.maxTurn) dA = -opts.maxTurn;

      // Ease the speed too — sudden force impulses no longer kick
      // the bird, they nudge it.
      const oldSpeed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      const targetSpeed = Math.sqrt(nvx * nvx + nvy * nvy);
      const speed = oldSpeed + (targetSpeed - oldSpeed) * opts.speedEase;

      const finalA = oldA + dA;
      p.vx = Math.cos(finalA) * speed;
      p.vy = Math.sin(finalA) * speed;

      // Pointer push is applied AFTER the boid easing so it acts as
      // a direct, immediate perturbation rather than being damped
      // by the per-frame turn/speed ease. This is what makes the
      // flock react crisply to a finger or cursor.
      if (pointer.active) {
        const px = p.x - pointer.x;
        const py = p.y - pointer.y;
        const pr = opts.pointerRadius;
        const pd2 = px * px + py * py;
        if (pd2 < pr * pr) {
          const pd = Math.sqrt(pd2) + 0.5;
          const t = 1 - pd / pr;
          const str = t * t * opts.pointerStrength;
          p.vx += (px / pd) * str;
          p.vy += (py / pd) * str;
        }
      }

      clampSpeed(p);
      p.x += p.vx;
      p.y += p.vy;

      // Wrap around all four edges so flocks orbit the canvas
      // rather than draining off one side. The wrap distance
      // covers the largest vision radius so neighbours across
      // the seam can still be near each other in the grid.
      if (p.x < -10) p.x += w + 20;
      else if (p.x > w + 10) p.x -= w + 20;
      if (p.y < -10) p.y += h + 20;
      else if (p.y > h + 10) p.y -= h + 20;
    }
  }

  function tick(now) {
    const dt = Math.min(50, now - last);
    last = now;
    time += dt;
    step();

    // Each particle's z drifts toward a slow 3D-ish noise field —
    // makes the flock undulate in apparent depth like a real
    // murmuration twisting against the sky.
    const tx = time * 0.00018;
    const ty = time * 0.00022;
    for (const p of particles) {
      const target =
        Math.sin(p.x * 0.0028 + tx) * 0.6 +
        Math.cos(p.y * 0.0034 + ty) * 0.5;
      p.z += (target - p.z) * opts.depthDrift;
    }

    ctx.fillStyle = opts.bg;
    ctx.fillRect(0, 0, w, h);

    ctx.fillStyle = `rgba(255, 255, 255, ${opts.pointAlpha})`;
    // Render at depth: near particles render larger, far smaller.
    // Single fillStyle, just varying the rect size — fast.
    for (const p of particles) {
      const size = opts.pointSize * (1 + p.z * opts.depthAmount * 0.5);
      if (size <= 0) continue;
      ctx.fillRect(p.x, p.y, size, size);
    }

    raf = requestAnimationFrame(tick);
  }

  function onPointerMove(e) {
    const r = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    pointer.x = t.clientX - r.left;
    pointer.y = t.clientY - r.top;
    pointer.active = true;
  }
  function onPointerLeave() {
    pointer.active = false;
  }

  resize();
  initParticles();
  window.addEventListener('resize', resize);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerleave', onPointerLeave);
  canvas.addEventListener('touchmove', onPointerMove, { passive: true });
  canvas.addEventListener('touchend', onPointerLeave);
  last = performance.now();
  raf = requestAnimationFrame(tick);

  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerleave', onPointerLeave);
    canvas.removeEventListener('touchmove', onPointerMove);
    canvas.removeEventListener('touchend', onPointerLeave);
  };
}
